import { FC, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card, CardContent, CardHeader, Grid, IconButton, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { useDialog } from 'src/hooks/use-dialog';
import { ConfirmationDialog } from 'src/components/confirmation-dialog';
import { PaginationAndFilter } from 'src/interfaces/common/pagination';
import { pincodeApi } from 'src/api/pincode';
import { Pincode } from 'src/interfaces/masters/pincode/pincode';
import { Scrollbar } from 'src/components/scrollbar';
import { AddTechnicianServiceArea } from 'src/components/new-technician/addTechnician/dialogs/addServiceArea';
import { InputField } from 'src/components/input-field';
import { ChevronDown as ChevronDownIcon } from '../../../../../icons/chevron-down';
import { ChevronLeft as ChevronLeftIcon } from '../../../../../icons/chevron-left';
import { Trash as TrashIcon } from '../../../../../icons/trash';
import { AddServiceArea } from '../../dialogs/addServiceArea';

interface ServiceAreaProps {
  scope?: string;
  cpServiceAreaIds?: string[];
  serviceAreaIds: string[];
  addServiceAreas: (data: string[]) => Promise<boolean>;
  deleteServiceArea: (data: string) => void;
}

export const ServiceAreaCard: FC<ServiceAreaProps> = (props) => {
  const { cpServiceAreaIds, serviceAreaIds, addServiceAreas, scope, deleteServiceArea } = props;
  const [serviceAreaToBeDeleted, setServiceAreaToBeDeleted] = useState<string>('');
  const [displayServices, setDisplayServices] = useState<boolean>(true);
  const [openServiceAreaSelectDialog, setOpenServiceAreaSelectDialog] = useState(false);
  const [openTechnicianServiceAreaSelectDialog,
    setOpenTechnicianServiceAreaSelectDialog] = useState(false);
  const [serviceAreaToBeSearch, setServiceAreaToBeSearch] = useState<string>('');
  const [serviceAreaList, setServiceAreaList] = useState<Pincode[]>([]);
  const [filteredServiceAreaList, setFilteredServiceAreaList] = useState<Pincode[]>([]);

  // Deduped display list by pincode (keep first occurrence)
  const dedupedServiceAreaList = useMemo(() => {
    const seen = new Set<number>();
    const result: Pincode[] = [];
    filteredServiceAreaList.forEach((item) => {
      if (!seen.has(item.code)) {
        seen.add(item.code);
        result.push(item);
      }
    });
    return result;
  }, [filteredServiceAreaList]);

  const getPincodesByServiceAreaIds = async (serviceAreaIdsArray: string[]) => {
    const pincodes = new Set<number>();
    serviceAreaIdsArray.forEach((item) => {
      const [pincode] = item.split('#');
      pincodes.add(Number(pincode));
    });

    const pincodeArray = Array.from(pincodes);
    const paginationObject: PaginationAndFilter = {
      filters: [
        {
          property: 'code',
          operator: 'in',
          value: pincodeArray,
        },
      ],
      pageSize: 9999,
      pageNumber: 1,
      sort: -1,
      sortBy: ''
    };

    const paginationResult = await pincodeApi.getPincodesByPaginationAndFilters(
      paginationObject
    );

    const pincodesRecords: Pincode[] = paginationResult?.responseData?.records || [];
    const tempStore: Pincode[] = [];

    for (let i = 0; i < serviceAreaIdsArray.length; i++) {
      const serviceAreaId = serviceAreaIdsArray[i];
      const hasArea = serviceAreaId.includes('#');

      for (let k = 0; k < pincodesRecords.length; k++) {
        const record = pincodesRecords[k];

        if (hasArea) {
          // For items with area (e.g., "411041#Nanded")
          if (serviceAreaId === `${record.code}#${record.area}`) {
            tempStore.push(record);
            break;
          }
        } else if (serviceAreaId === record.code.toString()) {
          // For items without area (e.g., "411001") show blank area in UI
          const recordToPush: Pincode = { ...record, area: '' };
          tempStore.push(recordToPush);
          break;
        }
      }
    }

    setServiceAreaList(tempStore);
    setFilteredServiceAreaList(tempStore);
  };

  useEffect(() => {
    getPincodesByServiceAreaIds(serviceAreaIds).catch(console.error);
  }, [serviceAreaIds?.length]);

  const handleDeleteItem = (id) => {
    setServiceAreaToBeDeleted(id);
  };
  const [
    deleteDialogOpen,
    handleOpenDeleteDialog,
    handleCloseDeleteDialog
  ] = useDialog();

  useEffect(() => {
    if (serviceAreaToBeDeleted) {
      handleOpenDeleteDialog();
    }
  }, [serviceAreaToBeDeleted]);

  const handleDeleteServiceArea = async () => {
    handleCloseDeleteDialog();
    await deleteServiceArea(encodeURIComponent(serviceAreaToBeDeleted));
  };

  const filterServiceArea = () => {
    const query = serviceAreaToBeSearch.trim();
    const filterData = query === ''
      ? serviceAreaList
      : serviceAreaList.filter((obj) => obj.code.toString().includes(query));
    setFilteredServiceAreaList(filterData);
  };

  useEffect(() => {
    if (serviceAreaToBeSearch !== '') {
      filterServiceArea();
    } else {
      setFilteredServiceAreaList(serviceAreaList);
    }
  }, [serviceAreaToBeSearch]);

  return (
    <>
      <Card
        variant="outlined"
        sx={{ mt: 1, }}
      >
        <CardHeader
          title="Service Areas"
          onClick={() => {
            setDisplayServices(!displayServices);
          }}
          action={(
            <>
              <Button
                color="primary"
                size="small"
                onClick={() => {
                  setDisplayServices(true);
                  if (scope === 'Technician') {
                    setOpenTechnicianServiceAreaSelectDialog(true);
                  } else {
                    setOpenServiceAreaSelectDialog(true);
                  }
                }}
                variant="contained"
              >
                Add
              </Button>
              { serviceAreaIds.length > 0 && (
                <>
                  { displayServices && (
                  <IconButton
                    sx={{ py: 0 }}
                    aria-label="view"
                    onClick={() => {
                      setDisplayServices(false);
                    }}
                  >
                    <ChevronDownIcon />
                  </IconButton>
                  )}
                  { !displayServices && (
                  <IconButton
                    sx={{ py: 0 }}
                    aria-label="view"
                    onClick={() => {
                      setDisplayServices(true);
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  )}
                </>
              )}
            </>
          )}
        />
        { serviceAreaIds.length > 0 && (
          <>
            { displayServices && (
            <CardContent>
              <Grid
                container
              >
                <Grid
                  item
                  md={4}
                  xs={12}
                >
                  <InputField
                    fullWidth
                    placeholder="Search By Pincode"
                    name="serviceAreaToBeSearch"
                    onChange={(event) => setServiceAreaToBeSearch(event.target.value)}
                    sx={{ mb: 1 }}
                    value={serviceAreaToBeSearch}
                  />
                </Grid>
                <Grid
                  item
                  md={12}
                  xs={12}
                >
                  <Card
                    variant="outlined"
                  >
                    <Scrollbar>
                      <TableContainer
                        sx={{
                          maxHeight: '220px',
                        }}
                        component={Paper}
                      >
                        <Table
                          size="small"
                          stickyHeader
                          aria-label="sticky table"
                        >
                          <TableHead>
                            <TableRow
                              key="HeaderRow"
                            >
                              <TableCell
                                width="20%"
                                sx={{
                                  backgroundColor: 'neutral.100',
                                }}
                              >
                                STATE
                              </TableCell>
                              <TableCell
                                width="20%"
                                sx={{
                                  backgroundColor: 'neutral.100',
                                }}
                              >
                                DISTRICT
                              </TableCell>
                              <TableCell
                                width="30%"
                                sx={{
                                  backgroundColor: 'neutral.100',
                                }}
                              >
                                CITY
                              </TableCell>
                              <TableCell
                                width="20%"
                                sx={{
                                  backgroundColor: 'neutral.100',
                                }}
                              >
                                PINCODE
                              </TableCell>
                              <TableCell
                                width="10%"
                                align="center"
                                sx={{
                                  backgroundColor: 'neutral.100',
                                }}
                              >
                                ACTION
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {dedupedServiceAreaList?.map((pincode) => (
                              <TableRow
                                key={`${pincode.code}`}
                              >
                                <TableCell>
                                  {pincode.state}
                                </TableCell>
                                <TableCell>
                                  {pincode.district}
                                </TableCell>
                                <TableCell>
                                  {pincode.city}
                                </TableCell>
                                <TableCell>
                                  {pincode.code}
                                </TableCell>
                                <TableCell
                                  align="center"
                                >
                                  <IconButton
                                    title="Delete Service Area"
                                    color="primary"
                                    onClick={() => handleDeleteItem(pincode.area ? `${pincode.code}#${pincode.area}` : `${pincode.code}`)}
                                    type="button"
                                    style={{ padding: 0 }}
                                  >
                                    <TrashIcon />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                    </Scrollbar>
                  </Card>
                </Grid>
              </Grid>

            </CardContent>
            )}
          </>
        )}
      </Card>
      {openServiceAreaSelectDialog && (
        <AddServiceArea
          open={openServiceAreaSelectDialog}
          onClose={() => setOpenServiceAreaSelectDialog(false)}
          addServiceAreas={addServiceAreas}
          existingServiceAreaIds={serviceAreaIds}
        />
      )}
      { openTechnicianServiceAreaSelectDialog && (
        <AddTechnicianServiceArea
          open={openTechnicianServiceAreaSelectDialog}
          onClose={() => setOpenTechnicianServiceAreaSelectDialog(false)}
          cpServiceAreaIds={cpServiceAreaIds}
          serviceAreaIds={serviceAreaIds}
          addServiceAreas={addServiceAreas}
        />
      )}
      <ConfirmationDialog
        message="Are you sure you want to delete this ServiceArea? This can't be undone."
        onCancel={() => {
          setServiceAreaToBeDeleted('');
          handleCloseDeleteDialog();
        }}
        onConfirm={handleDeleteServiceArea}
        open={deleteDialogOpen}
        title="Are You Sure?"
        variant="error"
      />
    </>
  );
};

ServiceAreaCard.propTypes = {
  cpServiceAreaIds: PropTypes.array,
  serviceAreaIds: PropTypes.array,
  scope: PropTypes.string,
  addServiceAreas: PropTypes.func,
  deleteServiceArea: PropTypes.func
};
